// src/utils/calcTileSize.js

/**
 * Calculates the optimal tile size and grid structure (rows/cols)
 * to maximize the video area within the container constraints.
 */
export function calculateTileLayout({
   participantCount,
   containerWidth,
   containerHeight,
   aspectRatio = 16 / 9,
   gap = 16,
   fillMode = false,
}) {
   if (!participantCount) {
      return { tileWidth: 0, tileHeight: 0, columns: 0, rows: 0 };
   }

   let bestLayout = {
      tileWidth: 0,
      tileHeight: 0,
      columns: 1,
      rows: 1,
      area: 0,
      score: 0,
   };

   // Try all possible row counts from 1 to participantCount
   for (let rows = 1; rows <= participantCount; rows++) {
      const columns = Math.ceil(participantCount / rows);

      // Calculate available space subtracting gaps
      // Gaps exist between items: (rows - 1) gaps vertically, (columns - 1) gaps horizontally
      const availableWidth = containerWidth - (columns - 1) * gap;
      const availableHeight = containerHeight - (rows - 1) * gap;

      if (availableWidth <= 0 || availableHeight <= 0) continue;

      // Calculate max potential dimensions based on this grid structure
      const maxTileWidth = availableWidth / columns;
      const maxTileHeight = availableHeight / rows;

      let tileWidth, tileHeight, area;

      if (fillMode) {
         // In fill mode, we care about the grid cell area, not the video fit
         tileWidth = maxTileWidth;
         tileHeight = maxTileHeight;
         // Total area coverage of the container by tiles
         area = tileWidth * tileHeight * participantCount;
      } else {
         // Standard mode: maximize the 16:9 video area inside the cell
         tileWidth = maxTileWidth;
         tileHeight = tileWidth / aspectRatio;

         // If calculated height exceeds the row height limit, scale down based on height
         if (tileHeight > maxTileHeight) {
            tileHeight = maxTileHeight;
            tileWidth = tileHeight * aspectRatio;
         }
         area = tileWidth * tileHeight * participantCount;
      }

      // Calculate efficiency: Ratio of participants to total slots
      const slots = rows * columns;
      const efficiency = participantCount / slots;

      // Calculate Aspect Ratio Score (how close is this tile to ideal 16:9?)
      const currentAspectRatio = tileWidth / tileHeight;
      const ratioDiff = Math.abs(currentAspectRatio - aspectRatio);

      // Drastic penalty for vertical strips (ratio < 1) or extreme horizontal strips (ratio > 3.5)
      let penalty = 1;
      if (currentAspectRatio < 1.0) {
         // Vertical tiles are very bad for video
         penalty = 0.5;
         if (currentAspectRatio < 0.6) penalty = 0.2; // Terrible strip
      } else if (currentAspectRatio > 3.5) {
         penalty = 0.5;
      }

      // Base aspect score: 0..1
      const baseAspectScore = 1 / (1 + ratioDiff);

      // Final aspect component
      const aspectComponent = baseAspectScore * penalty;

      // Final Score Calculation
      let score;
      if (fillMode) {
         // In fillMode:
         // area is always W*H (mostly), so efficiency and aspect ratio determine the winner.
         // We cube the aspect component to make it the DOMINANT factor.
         // We still want efficiency, but not at the cost of bad tiles.
         score = area * Math.pow(efficiency, 2) * Math.pow(aspectComponent, 3);
      } else {
         // Standard mode
         score = area * efficiency;
      }

      // Picking the layout that gives the best score
      // SPECIFIC FIX: For 2 users, always prefer 1 row (side-by-side) — consistent horizontal layout
      // regardless of container dimensions. Tiles still scale/shrink normally.
      const isPreferredForTwoUsers = participantCount === 2 && rows === 1;

      // SPECIFIC FIX: For 3 users, prioritize 1 row (side-by-side) in landscape/widescreen containers
      const isPreferredForThreeUsers =
         participantCount === 3 && rows === 1 && containerWidth > containerHeight * 0.9;

      const isPreferred = isPreferredForTwoUsers || isPreferredForThreeUsers;

      if (score > (bestLayout.score || -1) || isPreferred) {
         // If we already picked a preferred layout, don't let a "mathematically better"
         // but visually undesired layout override it unless it's also a preferred candidate
         if (
            bestLayout.rows === 1 &&
            (
               (bestLayout.participantCount === 2) ||
               (bestLayout.participantCount === 3 && !isPreferredForThreeUsers && bestLayout.containerWidth > bestLayout.containerHeight * 0.9)
            ) &&
            !isPreferred
         ) {
            // Keep the preferred horizontal layout
         } else {
            // Create a balanced row configuration
            const rowConfig = [];
            let remaining = participantCount;
            for (let r = 0; r < rows; r++) {
               const rowCount = Math.ceil(remaining / (rows - r));
               rowConfig.push(rowCount);
               remaining -= rowCount;
            }

            bestLayout = {
               tileWidth: fillMode ? maxTileWidth : tileWidth,
               tileHeight: fillMode ? maxTileHeight : tileHeight,
               columns,
               rows,
               rowConfig,
               area,
               score: isPreferred ? score + 1e12 : score, // Significantly boost preferred layouts
               // Store metadata for the keep-alive check above
               participantCount,
               containerWidth,
               containerHeight,
            };
         }
      }
   }

   return bestLayout;
}
